import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "../config/axios";
import PageWrapper from "../components/common/PageWrapper";
import Header from "../components/common/Header";
import BodyWrapper from "../components/common/BodyWrapper";
import Footer from "../components/common/Footer";
import ListingComponent from "../components/recipesPartials/ListingComponent";
import { useGeneral } from "../components/contexts/GeneralContext";
import ApiErrorMessage from "../components/common/ApiErrorMessage";
import NoItemsFound from "../components/common/NoItemsFound";
import SearchBar from "../components/common/SearchBar";
import GeneralButton from "../components/common/GeneralButton";
import FullScreenLoading from "../components/common/loading/FullScreenLoading";

// ---------------------- Custom Hook: Page Content ----------------------
const usePageContent = (page, query) => {
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState({ items: [], pagination: {} });
  const [error, setError] = useState("");
  const { discountCoupons } = useGeneral();

  useEffect(() => {
    let active = true;
    setLoading(true);

    (async () => {
      try {
        const { data } = await axios.get(`/recipes?page=${page}&q=${query}`);
        if (active) {
          setContent((prev) => ({
            items: page === 1 ? data.items : [...prev.items, ...data.items],
            pagination: data.pagination,
          }));
          setError("");
        }
      } catch (err) {
        console.error(err);
        if (active) setError("Failed to load recipes. Please try again later.");
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [page, query]);

  return { content, loading, error, discountCoupons };
};

// ---------------------- Custom Hook: Scroll to Top ----------------------
const useScrollToTop = () => {
  useEffect(() => {
    const timer = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
    return () => clearTimeout(timer);
  }, []);
};

// ---------------------- Main Component ----------------------
const RecipesListingPage = () => {
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [inputValue, setInputValue] = useState("");
  const { content, loading, error, discountCoupons } = usePageContent(
    page,
    query
  );

  // ---------------------- Handlers ----------------------
  const loadMore = useCallback(() => {
    if (page < content.pagination.pageCount && !loading)
      setPage((prev) => prev + 1);
  }, [page, content.pagination.pageCount, loading]);

  const hasMore = useMemo(
    () => page < content.pagination.pageCount,
    [page, content.pagination.pageCount]
  );

  const handleSearch = useCallback((searchQuery) => {
    setPage(1);
    setQuery(searchQuery);
  }, []);

  const handleClearSearch = useCallback(() => {
    setInputValue("");
    handleSearch("");
  }, [handleSearch]);

  useScrollToTop();

  // ---------------------- Memoized recipe cards ----------------------
  const recipeCards = useMemo(() => {
    return content.items.map((item) => (
      <ListingComponent key={item._id} item={item} />
    ));
  }, [content.items]);

  // ---------------------- Render ----------------------
  const hasItems = content?.pagination?.totalItems > 0;
  const hasQuery = !!query;

  return (
    <>
      {loading && page === 1 && <FullScreenLoading />}
      <PageWrapper loading={loading} className="">
        <Header code={discountCoupons} />
        <BodyWrapper loading={loading && page === 1} className="flex flex-col gap-2 md:gap-4 lg:gap-6">
          {hasItems && (
            <div className="flex flex-col items-start md:items-center justify-between gap- md:gap-4 md:mt-2 mt-0 ">
              <div className="flex flex-col md:flex-row md:items-center justify-start lg:justify-between gap-2 w-full">
               
                <div className="flex-1 flex items-center justify-end gap-2 w-full">
                  <SearchBar
                    value={inputValue}
                    setValue={setInputValue}
                    onClear={handleClearSearch}
                    onSearch={() => handleSearch(inputValue)}
                  />
                </div>
              </div>

              {hasQuery && (
                <div className="flex items-center flex-wrap gap-2 px-3 mt-2 md:mt-0 py-2 bg-[#D06F36]/10 rounded-lg w-full">
                  <p className="text-sm text-gray-700">
                    Showing results for{" "}
                    <span className="text-[#D06F36]">"{query}"</span>
                  </p>
                  <button
                    onClick={handleClearSearch}
                    className="ml-auto text-xs text-[#D06F36] font-medium hover:underline transition-all duration-200"
                  >
                    Clear All
                  </button>
                </div>
              )}
            </div>
          )}

          {!loading && error ? (
            <ApiErrorMessage
              message1="Failed to load recipes"
              message2="Please check your connection and try again."
            />
          ) : !loading && content?.items?.length <=0 ? (
            <NoItemsFound
              message1="No recipes available"
              message2="Please check back later."
            />
          ) : (
            hasItems && (
              <div className="rounded-[15px] bg-[#DEE2DD]/40 p-6 md:px-10 lg:px-12 lg:py-10 2xl:p-12 2xl:px-20 flex flex-col gap-2 md:gap-4 lg:gap-6">
                 <p className="text-34pt lg:text-36pt">Recipes</p>
                 <div className="flex flex-col gap-6  lg:gap-10 2xl:gap-12">

                
                <div
                  className={` 
                    ${
                      content.pagination.totalItems === 1
                        ? "grid grid-cols-1 gap-6"
                        : "grid xl:grid-cols-2 gap-2 md:gap-4 lg:gap-6"
                    }
                  `} 
                >
                  {recipeCards}
                </div>

                {hasMore && (
                  <div className="flex justify-center">
                    <GeneralButton
                      text="Load More"
                      title="Click to load more items"
                      onClick={loadMore}
                      bgColor="#D06F36"
                      disabled={loading}
                    />
                  </div>
                )}

                {loading && hasItems && page > 1 && (
                  <div className="flex justify-center mt-4">
                    <div className="w-5 h-5 border-4 border-tertiary border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
                 </div>
              </div>
            )
          )}
        </BodyWrapper>
        <Footer />
      </PageWrapper>
    </>
  );
};

export default RecipesListingPage;
